/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Plus, Trash2, GripVertical, Search, FolderPlus, FolderMinus, ChevronRight, ChevronLeft, Globe, Languages, Shield, ArrowUp, ArrowDown } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';

const AttributeSetFormEnhanced = ({ viewOnly = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isViewOnly = viewOnly;
  const { t, lang } = useTranslation();

  const getLocalizedLabel = (attr) => {
    // Check language-specific labels first, fall back to default_label
    let label = '';
    if (attr.labels && attr.labels[lang]) {
      label = attr.labels[lang];
    } else if (attr.default_label) {
      label = attr.default_label;
    }
    // Strip trailing digits from any label source
    return label.replace(/\d+$/, '');
  };
  const { success, error, warning, info } = useNotification();
  
  // Form basic data
  const [formData, setFormData] = useState({
    entity_type: 'product',
    set_code: '',
    set_name: '',
    is_system: 0,
  });
  
  // State for UI
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allAttributes, setAllAttributes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [groups, setGroups] = useState([
    { id: 1, label: 'General', attributes: [], collapsed: false }
  ]);
  const [nextGroupId, setNextGroupId] = useState(2);
  const [draggedAttribute, setDraggedAttribute] = useState(null);
  const [dragOverGroupId, setDragOverGroupId] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragOverIndexRef = useRef(null);
  const dragOverGroupIdRef = useRef(null);
  const [setLabels, setSetLabels] = useState({ en: '', zh: '' });
  
  // Wizard step state
  const [step, setStep] = useState(id ? 'configure' : 'select');
  const [availableSets, setAvailableSets] = useState([]);
  const [inheritFrom, setInheritFrom] = useState('');
  const [loadingSets, setLoadingSets] = useState(false);
  const [isInheriting, setIsInheriting] = useState(true);
  const [originalIsSystem, setOriginalIsSystem] = useState(0);
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // 1. Load product attribute sets for inherit dropdown
        const setsRes = await fetch('/api/settings/attribute-sets?entity_type=product');
        const setsData = await setsRes.json();
        if (setsData.status === 'success') {
          setAvailableSets(setsData.data || []);
        }
        
        // 2. Load all attributes
        const attributesRes = await fetch('/api/settings/attributes');
        const attributesData = await attributesRes.json();
        
        if (attributesData.status === 'success') {
          setAllAttributes(attributesData.data || []);
        } else {
          error('Failed to load attributes');
        }
        
        // 3. If editing, load attribute set data
        if (id) {
          const setRes = await fetch(`/api/settings/attribute-sets/${id}`);
          const setData = await setRes.json();
          
          if (setData.status === 'success' && setData.data) {
            const set = setData.data;
            setFormData({
              entity_type: set.entity_type || 'product',
              set_code: set.set_code || '',
              set_name: set.set_name || '',
              is_system: set.is_system ? 1 : 0,
            });
            setOriginalIsSystem(set.is_system ? 1 : 0);
            
            // Load set name translations
            if (set.set_labels) {
              setSetLabels({
                en: set.set_labels.en || '',
                zh: set.set_labels.zh || '',
              });
            }
            
            // 4. Process attributes into groups
            if (set.attributes && Array.isArray(set.attributes)) {
              // Group attributes by group_label
              const groupedByLabel = {};
              set.attributes.forEach(attr => {
                const groupLabel = attr.group_label || 'General';
                if (!groupedByLabel[groupLabel]) {
                  groupedByLabel[groupLabel] = [];
                }
                groupedByLabel[groupLabel].push({
                  ...attr,
                  dragId: `${attr.attribute_id}_${Date.now()}`
                });
              });
              
              // Convert to groups array
              const groupsArray = Object.keys(groupedByLabel).map((label, index) => {
                // Collect group_labels from the first attribute in this group
                const firstAttr = groupedByLabel[label][0];
                const grpMeta = set.group_meta || {};
                return {
                  id: index + 1,
                  label: label,
                  attributes: groupedByLabel[label],
                  is_system: (grpMeta[label] && grpMeta[label].is_system) ? 1 : 0,
                  sort_order: (grpMeta[label] && grpMeta[label].sort_order !== undefined) ? grpMeta[label].sort_order : index,
                  collapsed: false,
                  labels: firstAttr.group_labels || { en: '', zh: '' },
                  showLabelLang: false
                };
              });
              
              // Sort groups by sort_order to respect user's saved order
              groupsArray.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
              
              if (groupsArray.length > 0) {
                setGroups(groupsArray);
                setNextGroupId(groupsArray.length + 1);
              }
            }
          } else {
            error('Failed to load attribute set');
          }
        }
      } catch (err) {
        error('Network error while loading data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id]);
  
  // Filter attributes based on search term
  const filteredAttributes = allAttributes.filter(attr => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (attr.attribute_code && attr.attribute_code.toLowerCase().includes(term)) ||
      (attr.default_label && attr.default_label.toLowerCase().includes(term))
    );
  });
  
  // Get attributes not assigned to any group
  const assignedAttributeIds = new Set();
  groups.forEach(group => {
    group.attributes.forEach(attr => {
      assignedAttributeIds.add(attr.attribute_id);
    });
  });
  
  const unassignedAttributes = filteredAttributes.filter(
    attr => !assignedAttributeIds.has(attr.attribute_id)
  );
  
  // Drag and drop handlers
  const handleDragStart = (e, attribute, sourceGroupId) => {
    setDraggedAttribute({ ...attribute, _sourceGroupId: sourceGroupId });
    e.dataTransfer.setData('text/plain', JSON.stringify(attribute));
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDropOnGroup = (e, groupId) => {
    e.preventDefault();
    if (!draggedAttribute) return;
    
    const dropIndex = dragOverIndexRef.current;
    const targetGroupIndex = groups.findIndex(g => g.id === groupId);
    if (targetGroupIndex === -1) return;
    
    const sourceGroupId = draggedAttribute._sourceGroupId;
    const attrId = draggedAttribute.attribute_id;
    
    let updatedGroups = [...groups];
    const newAttr = { ...draggedAttribute, dragId: attrId + '_' + Date.now() };
    delete newAttr._sourceGroupId;
    
    // Remove from source group if cross-group
    if (sourceGroupId && sourceGroupId !== groupId) {
      const si = updatedGroups.findIndex(g => g.id === sourceGroupId);
      if (si !== -1) {
        updatedGroups[si] = { ...updatedGroups[si], attributes: updatedGroups[si].attributes.filter(a => a.attribute_id !== attrId) };
      }
    } else if (sourceGroupId === groupId) {
      // Within same group: remove from current position
      const ci = updatedGroups[targetGroupIndex].attributes.findIndex(a => a.attribute_id === attrId);
      if (ci !== -1) {
        updatedGroups[targetGroupIndex] = { ...updatedGroups[targetGroupIndex], attributes: updatedGroups[targetGroupIndex].attributes.filter(a => a.attribute_id !== attrId) };
        // If removed attr was before drop target, shift index
        if (ci < dropIndex) dropIndex--;
      }
    }
    
    // Re-find target group (may have shifted after source group removal)
    const finIdx = updatedGroups.findIndex(g => g.id === groupId);
    if (finIdx === -1) return;
    
    const targetAttrs = [...updatedGroups[finIdx].attributes];
    
    // If same group and dropIndex is valid (not null), insert at position
    // For different group, dropIndex may be null: append to end
    if (dropIndex !== null && dropIndex !== undefined && dropIndex >= 0 && dropIndex <= targetAttrs.length) {
      targetAttrs.splice(dropIndex, 0, newAttr);
    } else {
      targetAttrs.push(newAttr);
    }
    
    updatedGroups[finIdx] = { ...updatedGroups[finIdx], attributes: targetAttrs };
    setGroups(updatedGroups);
    setDraggedAttribute(null);
    setDragOverGroupId(null);
    setDragOverIndex(null);
  };

  const handleDragOverAttribute = (e, groupId, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    dragOverGroupIdRef.current = groupId;
    dragOverIndexRef.current = index;
    setDragOverGroupId(groupId);
    setDragOverIndex(index);
  };

  const handleDragLeaveAttribute = () => {
    dragOverGroupIdRef.current = null;
    dragOverIndexRef.current = null;
    setDragOverGroupId(null);
    setDragOverIndex(null);
  };
  
  const handleDropOnUnassigned = (e) => {
    e.preventDefault();
    if (!draggedAttribute) return;
    // Nothing to do here - attribute will be removed from its group automatically
    // when we update the groups state
    setDraggedAttribute(null);
  };
  
  const removeAttributeFromGroup = (groupId, attributeIndex) => {
    const updatedGroups = [...groups];
    updatedGroups.forEach(group => {
      if (group.id === groupId) {
        const attr = group.attributes[attributeIndex];
        if (attr && attr.is_system == 1) return; // Cannot remove system attributes
        group.attributes.splice(attributeIndex, 1);
      }
    });
    setGroups(updatedGroups);
  };
  
  const addNewGroup = () => {
    const maxSort = groups.reduce((max, g) => Math.max(max, g.sort_order ?? 0), 0);
    const newGroup = {
      id: nextGroupId,
      label: `Group ${nextGroupId}`,
      attributes: [],
      is_system: 0,
      sort_order: maxSort + 1,
      collapsed: false,
      labels: { en: '', zh: '' },
      showLabelLang: false
    };
    setGroups([...groups, newGroup]);
    setNextGroupId(nextGroupId + 1);
  };
  
  const removeGroup = (groupId) => {
    const targetGroup = groups.find(g => g.id === groupId);
    if (!targetGroup) return;
    
    // System groups cannot be deleted
    if (targetGroup.is_system == 1) {
      error(t('attribute_sets.system_group_cannot_delete'));
      return;
    }
    
    if (groups.length <= 1) {
      warning('At least one group is required');
      return;
    }
    
    if (!window.confirm('Delete this group? All attributes will be moved back to unassigned.')) {
      return;
    }
    
    const updatedGroups = groups.filter(group => group.id !== groupId);
    setGroups(updatedGroups);
    success('Group deleted successfully');
  };
  
  const updateGroupLabel = (groupId, newLabel) => {
    const updatedGroups = groups.map(group => {
      if (group.id === groupId) {
        return { ...group, label: newLabel };
      }
      return group;
    });
    setGroups(updatedGroups);
  };
  
  const toggleGroupCollapse = (groupId) => {
    const updatedGroups = groups.map(group => {
      if (group.id === groupId) {
        return { ...group, collapsed: !group.collapsed };
      }
      return group;
    });
    setGroups(updatedGroups);
  };
  
  const moveGroupUp = (groupIndex) => {
    if (groupIndex <= 0) return;
    const updatedGroups = [...groups];
    // Swap whole objects
    [updatedGroups[groupIndex - 1], updatedGroups[groupIndex]] = [updatedGroups[groupIndex], updatedGroups[groupIndex - 1]];
    // Update sort_order to match new positions
    updatedGroups[groupIndex - 1].sort_order = groupIndex - 1;
    updatedGroups[groupIndex].sort_order = groupIndex;
    setGroups(updatedGroups);
  };
  
  const moveGroupDown = (groupIndex) => {
    if (groupIndex >= groups.length - 1) return;
    const updatedGroups = [...groups];
    // Swap whole objects
    [updatedGroups[groupIndex], updatedGroups[groupIndex + 1]] = [updatedGroups[groupIndex + 1], updatedGroups[groupIndex]];
    // Update sort_order to match new positions
    updatedGroups[groupIndex].sort_order = groupIndex;
    updatedGroups[groupIndex + 1].sort_order = groupIndex + 1;
    setGroups(updatedGroups);
  };
  
  const moveAttributeWithinGroup = (groupId, fromIndex, toIndex) => {
    const updatedGroups = [...groups];
    const groupIndex = updatedGroups.findIndex(g => g.id === groupId);
    if (groupIndex === -1) return;
    
    const group = updatedGroups[groupIndex];
    const [movedAttribute] = group.attributes.splice(fromIndex, 1);
    group.attributes.splice(toIndex, 0, movedAttribute);
    
    setGroups(updatedGroups);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    // Basic validation
    if (!formData.set_code.trim()) {
      error('Attribute set code is required');
      return;
    }
    if (!formData.set_name.trim()) {
      error('Attribute set name is required');
      return;
    }
    
    // Prepare group metadata
    const groupMetaData = [];
    groups.forEach((group, idx) => {
      groupMetaData.push({
        group_label: group.label,
        sort_order: group.sort_order ?? idx,
        is_system: group.is_system || 0
      });
    });
    
    // Prepare attributes data with group labels
    const attributesData = [];
    groups.forEach(group => {
      group.attributes.forEach((attr, index) => {
        attributesData.push({
          attribute_id: attr.attribute_id,
          group_label: group.label || 'General',
          sort_order: index,
          group_labels: group.labels || { en: '', zh: '' }
        });
      });
    });
    
    const payload = {
      ...formData,
      set_labels: setLabels,
      group_meta: groupMetaData,
      attributes: attributesData
    };
    
    const endpoint = id ? `/api/settings/attribute-sets/${id}/edit` : '/api/settings/attribute-sets/add';
    const method = id ? 'PUT' : 'POST';
    
    setIsSubmitting(true);
    try {
      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        success(id ? 'Attribute set updated successfully' : 'Attribute set created successfully');
        navigate('/settings/attribute-sets');
      } else {
        error(data.message || 'Failed to save attribute set');
      }
    } catch (err) {
      error('Network error while saving attribute set');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle "Next" button: load inherited set or start fresh
  const handleNext = async () => {
    setLoading(true);
    try {
      if (inheritFrom) {
        // Load the source attribute set
        const res = await fetch(`/api/settings/attribute-sets/${inheritFrom}`);
        const data = await res.json();
        
        if (data.status === 'success' && data.data) {
          const sourceSet = data.data;
          
          // Process attributes into groups (same logic as edit mode)
          if (sourceSet.attributes && Array.isArray(sourceSet.attributes)) {
            const groupedByLabel = {};
            sourceSet.attributes.forEach(attr => {
              const groupLabel = attr.group_label || 'General';
              if (!groupedByLabel[groupLabel]) {
                groupedByLabel[groupLabel] = [];
              }
              groupedByLabel[groupLabel].push({
                ...attr,
                dragId: `${attr.attribute_id}_${Date.now()}_${Math.random()}`
              });
            });
            
            const grpMeta = sourceSet.group_meta || {};
            let groupsArray = Object.keys(groupedByLabel).map((label, index) => {
              const firstAttr = groupedByLabel[label][0];
              return {
                id: index + 1,
                label: label,
                attributes: groupedByLabel[label],
                is_system: (grpMeta[label] && grpMeta[label].is_system) ? 1 : 0,
                sort_order: (grpMeta[label] && grpMeta[label].sort_order !== undefined) ? grpMeta[label].sort_order : index,
                collapsed: false,
                labels: firstAttr.group_labels || { en: '', zh: '' },
                showLabelLang: false
              };
            });
            groupsArray.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
            
            if (groupsArray.length > 0) {
              setGroups(groupsArray);
              setNextGroupId(groupsArray.length + 1);
            }
          }
        }
      }
      setStep('configure');
    } catch (err) {
      error('Failed to load inherited attribute set');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && step === 'select' && !inheritFrom) {
    // Still loading sets/attributes
    return <div className="text-center p-10">{t('common.loading')}</div>;
  }
  
  // Step 1: Entity type + Inherit selection
  if (step === 'select') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">{t('settings.create_attribute_set')}</h1>
          <button 
            onClick={() => navigate('/settings/attribute-sets')} 
            className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300">{t('attribute_sets.create')}</h2>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('attribute_sets.entity_type')} <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
              value={formData.entity_type}
              onChange={e => setFormData({...formData, entity_type: e.target.value})}
            >
              <option value="product">{t('attribute_sets.product')}</option>
            </select>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {t('attribute_sets.entity_type_hint')}
            </p>
          </div>
          
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="inheritToggle"
                checked={isInheriting}
                onChange={(e) => setIsInheriting(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="inheritToggle" className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                Inherit from an existing Product attribute set
              </label>
            </div>
            
            {isInheriting && (
              <div className="space-y-2 ml-7">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Inherit From <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                  value={inheritFrom}
                  onChange={e => setInheritFrom(e.target.value)}
                >
                  <option value="">{t('attribute_sets.inherit_from')}</option>
                  {availableSets.map(set => (
                    <option key={set.set_id} value={set.set_id}>
                      {set.set_name} ({set.set_code})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  All groups, attribute assignments, and ordering will be copied.
                </p>
              </div>
            )}
            
            {!isInheriting && (
              <div className="ml-7 px-4 py-3 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Start with an empty attribute set with a single &quot;General&quot; group. You can add groups and attributes later.
                </p>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/settings/attribute-sets')}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={isInheriting && !inheritFrom}
            className={'px-6 py-2 rounded-lg transition-colors ' + (isInheriting && !inheritFrom ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700')}
          >
            Next
          </button>
        </div>
      </div>
    );
  }
  
  // Step 2: Configure groups and attributes (existing form)
  if (loading) {
    return <div className="text-center p-10">{t('common.loading')}</div>;
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">
          {isViewOnly ? t('attribute_sets.view') : (id ? t('settings.edit_attribute_set') : t('settings.create_attribute_set'))}
        </h1>
        <button 
          onClick={() => navigate('/settings/attribute-sets')} 
          className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full"
        >
          <X size={20} />
        </button>
      </div>
      
      {/* Basic Info Form */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
        <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300">{t('attribute_sets.basic_info')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('attribute_sets.entity_type')} <span className="text-red-500">*</span>
            </label>
            <select 
              className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
              value={formData.entity_type}
              onChange={e => setFormData({...formData, entity_type: e.target.value})}
              disabled={!!id || isViewOnly}
              required
            >
              <option value="product">{t('attribute_sets.product')}</option>
              <option value="supplier">{t('attribute_sets.supplier')}</option>
              <option value="employee">{t('attribute_sets.employee')}</option>
            </select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('attribute_sets.set_code')} <span className="text-red-500">*</span>
            </label>
            <input 
              className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
              value={formData.set_code}
              onChange={e => setFormData({...formData, set_code: e.target.value})}
              placeholder={t('attribute_sets.code_placeholder')}
              readOnly={!!id || isViewOnly}
              required
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('attribute_sets.set_name')} <span className="text-red-500">*</span>
            </label>
            <input 
              className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
              value={formData.set_name}
              onChange={e => setFormData({...formData, set_name: e.target.value})}
              placeholder={t('attribute_sets.name_placeholder')}
              readOnly={isViewOnly}
              required
            />
          </div>
        </div>
        
        {/* {t('attribute_sets.system_attribute_set')} Toggle — 编辑模式不允许修改 */}
        {!isViewOnly && (
          <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="systemSetToggle"
                checked={formData.is_system == 1}
                onChange={e => setFormData({...formData, is_system: e.target.checked ? 1 : 0})}
                disabled={!!id && originalIsSystem == 1}
                className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-amber-500 focus:ring-amber-500"
              />
              <label htmlFor="systemSetToggle" className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer flex items-center gap-2">
                {t('attribute_sets.system_attribute_set')}
                {formData.is_system == 1 && (
                  <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded">{t('attribute_sets.system')}</span>
                )}
              </label>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {t('attribute_sets.system_cannot_delete_detail')}
              </span>
            </div>
          </div>
        )}
        {isViewOnly && formData.is_system == 1 && (
          <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
            <span className="px-3 py-1 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded">{t('attribute_sets.system')}</span>
          </div>
        )}
        
        {/* Set Name Translations */}
        <div className="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-700">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('attribute_sets.set_name_translations')}</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs text-slate-500 dark:text-slate-400">{t('attribute_sets.english')}</label>
              <input 
                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent text-sm"
                value={setLabels.en || ''}
                onChange={e => setSetLabels({...setLabels, en: e.target.value})}
                placeholder={t('attribute_sets.set_name_default_placeholder')}
                readOnly={isViewOnly}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500 dark:text-slate-400">{t('attribute_sets.chinese')}</label>
              <input 
                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent text-sm"
                value={setLabels.zh || ''}
                onChange={e => setSetLabels({...setLabels, zh: e.target.value})}
                placeholder={t('eav.default_product_set')}
                readOnly={isViewOnly}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Attribute Assignment - Magento 2 Style */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel: {t('attribute_sets.unassigned_attributes')} */}
        <div className="lg:col-span-1 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-800">
            <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
              {t('attribute_sets.unassigned_attributes')} ({unassignedAttributes.length})
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {isViewOnly ? t('attribute_sets.unassigned_attributes_hint') : t('attribute_sets.drag_hint')}
            </p>
          </div>
          
          {/* Search */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input 
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-transparent text-sm"
                placeholder={t('attribute_sets.search_attributes')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          {/* Attributes List */}
          <div 
            className="p-4 max-h-[500px] overflow-y-auto"
            onDragOver={!isViewOnly ? handleDragOver : undefined}
            onDrop={!isViewOnly ? handleDropOnUnassigned : undefined}
          >
            {unassignedAttributes.length === 0 ? (
              <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                {searchTerm ? t('attribute_sets.no_matching_attributes') : t('attribute_sets.all_attributes_assigned')}
              </div>
            ) : (
              <div className="space-y-2">
                {unassignedAttributes.map(attr => (
                  <div
                    key={attr.attribute_id}
                    draggable={!isViewOnly}
                    onDragStart={(e) => handleDragStart(e, attr)}
                    className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-move group"
                  >
                    {!isViewOnly && <GripVertical className="text-slate-400 group-hover:text-slate-600" size={16} />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{getLocalizedLabel(attr)}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate">
                        {attr.attribute_code}
                      </div>
                    </div>
                    <div className="text-xs px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                      {attr.input_type}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Right Panel: Groups */}
        <div className="lg:col-span-2 space-y-4">
          {/* Groups Header */}
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
              {t('attribute_sets.attribute_groups', {count: groups.length})}
            </h2>
            {!isViewOnly && (
              <button
                type="button"
                onClick={addNewGroup}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
              >
                <Plus size={16} />
                Add Group
              </button>
            )}
          </div>
          
          {/* Groups List */}
          <div className="space-y-4">
            {groups.map((group, groupIndex) => (
              <div 
                key={group.id}
                className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden"
                onDragOver={!isViewOnly ? handleDragOver : undefined}
                onDrop={!isViewOnly ? (e) => handleDropOnGroup(e, group.id) : undefined}
                onDragLeave={!isViewOnly ? handleDragLeaveAttribute : undefined}
              >
                {/* Group Header */}
                <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <button
                      type="button"
                      onClick={() => toggleGroupCollapse(group.id)}
                      className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                    >
                      {group.collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                    </button>
                    
                    <div className="flex items-center gap-2 w-full">
                      <input
                        type="text"
                        value={group.label}
                        onChange={(e) => updateGroupLabel(group.id, e.target.value)}
                        className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-0"
                        readOnly={isViewOnly}
                      />
                      {!isViewOnly && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = [...groups];
                              const idx = updated.findIndex(g => g.id === group.id);
                              if (idx !== -1) {
                                updated[idx] = { ...updated[idx], showLabelLang: !updated[idx].showLabelLang };
                                setGroups(updated);
                              }
                            }}
                            className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-400 hover:text-blue-500 transition-colors"
                            title={t('attribute_sets.group_label_translations')}
                          >
                            <Globe size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = [...groups];
                              const idx = updated.findIndex(g => g.id === group.id);
                              if (idx !== -1) {
                                updated[idx] = { ...updated[idx], is_system: updated[idx].is_system == 1 ? 0 : 1 };
                                setGroups(updated);
                              }
                            }}
                            className={'p-1.5 rounded transition-colors ' + (group.is_system == 1 ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700')}
                            title={group.is_system == 1 ? 'System group (click to unset)' : 'Mark as system group'}
                          >
                            <Shield size={16} />
                          </button>
                        </>
                      )}
                      {group.is_system == 1 && (
                        <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded">{t('attribute_sets.system')}</span>
                      )}
                    </div>
                    
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      ({group.attributes.length} attributes)
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {!isViewOnly && (
                      <>
                        <button
                          type="button"
                          onClick={() => moveGroupUp(groupIndex)}
                          disabled={groupIndex === 0}
                          className="p-1 text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveGroupDown(groupIndex)}
                          disabled={groupIndex >= groups.length - 1}
                          className="p-1 text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <ArrowDown size={16} />
                        </button>
                        {group.is_system != 1 && (
                          <button
                            type="button"
                            onClick={() => removeGroup(group.id)}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                            title={t('attribute_sets.remove_group')}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {/* Group Label Translations */}
                {group.showLabelLang && (
                  <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 block">
                      Group Label Translations
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-xs text-slate-400">{t('attribute_sets.english')}</label>
                        <input
                          type="text"
                          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent text-sm"
                          value={(group.labels && group.labels.en) || ''}
                          onChange={(e) => {
                            if (isViewOnly) return;
                            const updated = [...groups];
                            const idx = updated.findIndex(g => g.id === group.id);
                            if (idx !== -1) {
                              updated[idx] = {
                                ...updated[idx],
                                labels: { ...updated[idx].labels, en: e.target.value }
                              };
                              setGroups(updated);
                            }
                          }}
                          placeholder={t('attribute_sets.group_label_placeholder')}
                          readOnly={isViewOnly}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-slate-400">{t('attribute_sets.chinese')}</label>
                        <input
                          type="text"
                          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent text-sm"
                          value={(group.labels && group.labels.zh) || ''}
                          onChange={(e) => {
                            if (isViewOnly) return;
                            const updated = [...groups];
                            const idx = updated.findIndex(g => g.id === group.id);
                            if (idx !== -1) {
                              updated[idx] = {
                                ...updated[idx],
                                labels: { ...updated[idx].labels, zh: e.target.value }
                              };
                              setGroups(updated);
                            }
                          }}
                          placeholder={t('attribute_sets.group_placeholder')}
                          readOnly={isViewOnly}
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Group Content */}
                {!group.collapsed && (
                  <div className="p-4">
                    {group.attributes.length === 0 ? (
                      <div 
                        className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-8 text-center"
                        onDragOver={!isViewOnly ? handleDragOver : undefined}
                        onDrop={!isViewOnly ? (e) => handleDropOnGroup(e, group.id) : undefined}
                        onDragLeave={!isViewOnly ? handleDragLeaveAttribute : undefined}
                      >
                        <p className="text-slate-400 dark:text-slate-500 mb-2">
                          Drop attributes here
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Drag attributes from the left panel
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {group.attributes.map((attr, attrIndex) => (
                          <div
                            key={attr.dragId || attr.attribute_id}
                            draggable={!isViewOnly}
                            onDragStart={(e) => handleDragStart(e, attr, group.id)}
                            onDragOver={(e) => handleDragOverAttribute(e, group.id, attrIndex)}
                            onDrop={(e) => handleDropOnGroup(e, group.id)}
                            className={'flex items-center gap-3 p-3 border rounded-lg group cursor-move ' + (dragOverGroupId === group.id && dragOverIndex === attrIndex ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-400' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800')}
                          >
                            {!isViewOnly && <GripVertical className="text-slate-400 cursor-move" size={16} />}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{getLocalizedLabel(attr)}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                                {attr.attribute_code}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-xs px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                                {attr.input_type}
                              </div>
                              {attr.is_system == 1 ? (
                                <span className="px-2 py-1 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded" title={t('attribute_sets.system_cannot_remove')}>
                                  {t('attribute_sets.system')}
                                </span>
                              ) : (
                                !isViewOnly && (
                                  <button
                                    type="button"
                                    onClick={() => removeAttributeFromGroup(group.id, attrIndex)}
                                    className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                                    title={t('attribute_sets.remove_from_group')}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Form Actions */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex justify-end gap-3">
          {isViewOnly ? (
            <button 
              type="button" 
              onClick={() => navigate('/settings/attribute-sets')} 
              className="px-6 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
            >
              {t('common.back_to_list')}
            </button>
          ) : (
            <>
              <button 
                type="button" 
                onClick={() => navigate('/settings/attribute-sets')} 
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-6 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('common.processing')}
                  </span>
                ) : (
                  <><Save size={18} />{id ? t('common.update') : t('common.add')}</>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttributeSetFormEnhanced;